import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { 
  Plus, 
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  Eye,
  RefreshCw,
  UserPlus,
  ArrowRightLeft,
  TrendingUp,
  LogOut,
  FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const ACTION_TYPES = [
  { code: '01', name: 'Hiring', icon: UserPlus, color: 'text-green-600' },
  { code: '02', name: 'Rehiring', icon: UserPlus, color: 'text-green-500' },
  { code: '03', name: 'Transfer', icon: ArrowRightLeft, color: 'text-orange-600' },
  { code: '04', name: 'Promotion', icon: TrendingUp, color: 'text-orange-600' },
  { code: '05', name: 'Demotion', icon: TrendingUp, color: 'text-orange-600' },
  { code: '06', name: 'Org Reassignment', icon: ArrowRightLeft, color: 'text-orange-600' },
  { code: '07', name: 'Pay Change', icon: FileText, color: 'text-orange-600' },
  { code: '08', name: 'Leave of Absence', icon: Clock, color: 'text-yellow-600' },
  { code: '09', name: 'Return from Leave', icon: CheckCircle, color: 'text-orange-600' },
  { code: '10', name: 'Termination', icon: LogOut, color: 'text-red-600' },
  { code: '11', name: 'Retirement', icon: LogOut, color: 'text-gray-600' },
  { code: '12', name: 'Probation End', icon: CheckCircle, color: 'text-lime-600' },
  { code: '13', name: 'Confirmation', icon: CheckCircle, color: 'text-emerald-600' },
  { code: '14', name: 'Contract Change', icon: FileText, color: 'text-orange-600' },
];

const STATUS_CONFIG = {
  'DRAFT': { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
  'PENDING_APPROVAL': { color: 'bg-yellow-100 text-yellow-700', label: 'Pending Approval' },
  'APPROVED': { color: 'bg-orange-100 text-orange-700', label: 'Approved' },
  'REJECTED': { color: 'bg-red-100 text-red-700', label: 'Rejected' },
  'COMPLETED': { color: 'bg-green-100 text-green-700', label: 'Completed' },
  'CANCELLED': { color: 'bg-gray-200 text-gray-500', label: 'Cancelled' },
};

const PersonnelActions = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [actions, setActions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState('ALL');
  
  // Form state
  const [formData, setFormData] = useState({
    employee_id: '',
    action_type: '',
    effective_date: '',
    remarks: '',
    new_position: '',
    new_organizational_unit: '',
    new_company_code: '',
    new_personnel_area: '',
    new_basic_pay: '',
    new_currency: 'INR',
    termination_date: '',
    last_working_day: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;


  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [actionsRes, employeesRes, pendingRes] = await Promise.all([
        axios.get(`${backendUrl}/api/hcm/personnel-actions${filterStatus !== 'ALL' ? `?status=${filterStatus}` : ''}`),
        axios.get(`${backendUrl}/api/hcm/employees`),
        axios.get(`${backendUrl}/api/hcm/personnel-actions/pending-count`)
      ]);
      setActions(actionsRes.data);
      setEmployees(employeesRes.data);
      setPendingCount(pendingRes.data.pending_count);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load personnel actions');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, backendUrl]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateAction = async () => {
    try {
      if (!formData.employee_id || !formData.action_type || !formData.effective_date) {
        toast.error('Please fill in required fields');
        return;
      }

      const payload = {
        employee_id: formData.employee_id,
        action_type: formData.action_type,
        effective_date: formData.effective_date,
        remarks: formData.remarks || null,
        new_position: formData.new_position || null,
        new_organizational_unit: formData.new_organizational_unit || null,
        new_company_code: formData.new_company_code || null,
        new_personnel_area: formData.new_personnel_area || null,
        new_basic_pay: formData.new_basic_pay ? parseFloat(formData.new_basic_pay) : null,
        new_currency: formData.new_currency || null,
        termination_date: formData.termination_date || null,
        last_working_day: formData.last_working_day || null
      };

      const res = await axios.post(`${backendUrl}/api/hcm/personnel-actions`, payload);
      toast.success(res.data.message);
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating action:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create action');
      }
    }
  };

  const handleApprove = async (actionId) => {
    try {
      const res = await axios.post(`${backendUrl}/api/hcm/personnel-actions/${actionId}/approve`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to approve action');
      }
    }
  };

  const handleExecute = async (actionId) => {
    try {
      const res = await axios.post(`${backendUrl}/api/hcm/personnel-actions/${actionId}/execute`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to execute action');
      }
    }
  };

  const handleSubmitForApproval = async (actionId) => {
    try {
      const res = await axios.post(`${backendUrl}/api/hcm/personnel-actions/${actionId}/submit`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to submit action');
      }
    }
  };

  const handleCancel = async (actionId) => {
    if (!window.confirm('Are you sure you want to cancel this action?')) return;
    try {
      const res = await axios.post(`${backendUrl}/api/hcm/personnel-actions/${actionId}/cancel`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to cancel action');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      action_type: '',
      effective_date: '',
      remarks: '',
      new_position: '',
      new_organizational_unit: '',
      new_company_code: '',
      new_personnel_area: '',
      new_basic_pay: '',
      new_currency: 'INR',
      termination_date: '',
      last_working_day: ''
    });
  };

  const getActionTypeInfo = (code) => {
    return ACTION_TYPES.find(t => t.code === code) || { name: code, icon: FileText, color: 'text-gray-600' };
  };

  const renderActionFields = () => {
    const actionType = formData.action_type;
    
    // Show different fields based on action type
    if (['03', '04', '05', '06'].includes(actionType)) {
      // Transfer, Promotion, Demotion, Org Reassignment
      return (
        <>
          <div>
            <Label>New Position</Label>
            <Input
              value={formData.new_position}
              onChange={(e) => setFormData({ ...formData, new_position: e.target.value })}
              placeholder="New position title"
            />
          </div>
          <div>
            <Label>New Department</Label>
            <Input
              value={formData.new_organizational_unit}
              onChange={(e) => setFormData({ ...formData, new_organizational_unit: e.target.value })}
              placeholder="New department/org unit"
            />
          </div>
        </>
      );
    }
    
    if (actionType === '07') {
      // Pay Change
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>New Basic Pay</Label>
            <Input
              type="number"
              value={formData.new_basic_pay}
              onChange={(e) => setFormData({ ...formData, new_basic_pay: e.target.value })}
              placeholder="0.00"
            />
          </div>
          <div>
            <Label>Currency</Label>
            <Select
              value={formData.new_currency}
              onValueChange={(v) => setFormData({ ...formData, new_currency: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INR">INR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }
    
    if (['10', '11'].includes(actionType)) {
      // Termination, Retirement
      return (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Termination Date</Label>
            <Input
              type="date"
              value={formData.termination_date}
              onChange={(e) => setFormData({ ...formData, termination_date: e.target.value })}
            />
          </div>
          <div>
            <Label>Last Working Day</Label>
            <Input
              type="date"
              value={formData.last_working_day}
              onChange={(e) => setFormData({ ...formData, last_working_day: e.target.value })}
            />
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="personnel-actions-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Personnel Actions</h1>
            <p className="text-gray-500">Manage employee lifecycle events (Infotype 0000)</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              {canWrite('personnel_action') && (
              <Button 
                className="bg-stone-800 hover:bg-stone-700"
                onClick={() => { resetForm(); setIsDialogOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Action
              </Button>
              )}
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Personnel Action</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Employee *</Label>
                    <Select
                      value={formData.employee_id}
                      onValueChange={(v) => setFormData({ ...formData, employee_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.employee_id} value={emp.employee_id}>
                            {emp.employee_id} - {emp.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Effective Date *</Label>
                    <Input
                      type="date"
                      value={formData.effective_date}
                      onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Action Type *</Label>
                  <Select
                    value={formData.action_type}
                    onValueChange={(v) => setFormData({ ...formData, action_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select action type" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTION_TYPES.map(type => {
                        const Icon = type.icon;
                        return (
                          <SelectItem key={type.code} value={type.code}>
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${type.color}`} />
                              {type.name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {renderActionFields()}

                <div>
                  <Label>Remarks</Label>
                  <Textarea
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder="Additional notes or comments..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleCreateAction} className="flex-1 bg-stone-800 hover:bg-stone-700">
                    Create Action
                  </Button>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Actions</p>
                  <p className="text-2xl font-bold">{actions.length}</p>
                </div>
                <FileText className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold">{pendingCount}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-2xl font-bold">{actions.filter(a => a.status === 'COMPLETED').length}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-gray-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Draft</p>
                  <p className="text-2xl font-bold">{actions.filter(a => a.status === 'DRAFT').length}</p>
                </div>
                <FileText className="w-8 h-8 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <Label>Filter by Status:</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Status</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="PENDING_APPROVAL">Pending Approval</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="COMPLETED">Completed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Actions Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : actions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-500">
                <FileText className="w-12 h-12 mb-3 text-gray-300" />
                <p>No personnel actions found</p>
                <p className="text-sm">Create a new action to get started</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Action ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Employee</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Action Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Effective Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Created</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.map((action) => {
                    const typeInfo = getActionTypeInfo(action.action_type);
                    const Icon = typeInfo.icon;
                    const statusConfig = STATUS_CONFIG[action.status] || STATUS_CONFIG.DRAFT;
                    
                    return (
                      <tr key={action.action_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <span className="font-mono text-sm font-semibold">{action.action_id}</span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="font-medium">{action.employee_name || action.employee_id}</div>
                          <div className="text-xs text-gray-500">{action.employee_id}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${typeInfo.color}`} />
                            <span>{action.action_type_name || typeInfo.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {new Date(action.effective_date).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={statusConfig.color}>
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {new Date(action.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1">
                            {action.status === 'DRAFT' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSubmitForApproval(action.action_id)}
                                  title="Submit for Approval"
                                  className="text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancel(action.action_id)}
                                  title="Cancel"
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {action.status === 'PENDING_APPROVAL' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleApprove(action.action_id)}
                                  title="Approve"
                                  className="text-green-600 hover:text-green-800 hover:bg-green-50"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCancel(action.action_id)}
                                  title="Reject"
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {action.status === 'APPROVED' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleExecute(action.action_id)}
                                title="Execute"
                                className="text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/hcm/employees/${action.employee_id}`)}
                              title="View Employee"
                              className="text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PersonnelActions;
